import React, { useState, useEffect, useRef } from 'react';
import TextField from '@decisiv/ui-components/lib/components/TextField';

const dropdownStyle = {
  position:    'absolute',
  top:         '100%',
  left:        0,
  right:       0,
  zIndex:      1000,
  background:  '#fff',
  border:      '1px solid #d0d0d0',
  borderRadius: 4,
  boxShadow:   '0 4px 12px rgba(0,0,0,0.12)',
  maxHeight:   240,
  overflowY:   'auto',
  listStyle:   'none',
  margin:      0,
  padding:     0,
};

const optionStyle = (highlighted) => ({
  padding:      '10px 12px',
  cursor:       'pointer',
  background:   highlighted ? '#f0f4ff' : 'transparent',
  borderBottom: '1px solid #f0f0f0',
});

/**
 * Generic debounced typeahead that fetches `?q=` from searchEndpoint.
 * The endpoint must return [{uuid, email, username}] or any [{id, label, sublabel?}].
 *
 * @param {string}   label           Field label shown above the input.
 * @param {string}   searchEndpoint  URL to GET with ?q= query param.
 * @param {string}   value           Currently selected identifier (e.g. UUID).
 * @param {string}   displayValue    Display string shown in the input.
 * @param {function} onChange        Called with (value, displayValue) on selection or clear.
 * @param {string}   [helpMessage]
 */
const AdminTypeahead = ({ label, searchEndpoint, value, displayValue = '', onChange, helpMessage }) => {
  const [query, setQuery]               = useState(displayValue);
  const [results, setResults]           = useState([]);
  const [open, setOpen]                 = useState(false);
  const [highlighted, setHighlighted]   = useState(-1);
  const [loading, setLoading]           = useState(false);
  const [fetchError, setFetchError]     = useState(false);
  const containerRef                    = useRef(null);
  const debounceRef                     = useRef(null);

  useEffect(() => { setQuery(displayValue); }, [displayValue]);

  useEffect(() => {
    clearTimeout(debounceRef.current);

    if (query.length < 2) { setResults([]); setOpen(false); setFetchError(false); return; }
    if (query === displayValue && value) { setResults([]); setOpen(false); return; }

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setFetchError(false);
      fetch(`${searchEndpoint}?q=${encodeURIComponent(query)}`)
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then((data) => { setResults(data); setOpen(data.length > 0); setHighlighted(-1); })
        .catch(() => { setFetchError(true); setResults([]); setOpen(false); })
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectResult = (item) => {
    const display = item.email ?? item.label ?? String(item.id ?? item.uuid);
    setQuery(display);
    setOpen(false);
    setResults([]);
    onChange(item.uuid ?? item.id, display);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val === '') onChange('', '');
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); selectResult(results[highlighted]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <TextField
        label={label}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        helpMessage={helpMessage !== undefined ? helpMessage : 'Search by typing (min 2 characters)'}
        placeholder={`Search ${label.toLowerCase()}…`}
        loading={loading}
      />

      {fetchError && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#c0392b' }}>
          Search failed — please try again.
        </div>
      )}

      {open && results.length > 0 && (
        <ul style={dropdownStyle}>
          {results.map((item, i) => {
            const primary   = item.email   ?? item.label   ?? String(item.id ?? item.uuid);
            const secondary = item.username ?? item.sublabel;
            const key       = item.uuid    ?? item.id      ?? i;
            return (
              <li
                key={key}
                style={optionStyle(i === highlighted)}
                onMouseDown={() => selectResult(item)}
                onMouseEnter={() => setHighlighted(i)}
              >
                <span style={{ fontWeight: 500 }}>{primary}</span>
                {secondary && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>{secondary}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminTypeahead;
